import type { Tournament } from '@repo/shared-types';
import { clubApi } from '@repo/api-client';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

// The app layout loads the club once per club id, so a status change elsewhere
// bumps this for the sidebar to fetch the tournaments again.
const activeTournamentsVersion = atom(0);

/** The tournaments being played, the one that started first first. */
export const activeTournaments = (tournaments: Tournament[]) =>
  tournaments
    .filter((tournament) => tournament.status === 'ACTIVE')
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

export function useActiveTournaments(clubId: string) {
  const version = useAtomValue(activeTournamentsVersion);
  const [loaded, setLoaded] = useState<{ clubId: string; tournaments: Tournament[] } | null>(null);

  useEffect(() => {
    let current = true;
    clubApi.getTournaments(clubId).then(
      ({ results }) => current && setLoaded({ clubId, tournaments: activeTournaments(results) }),
      // Without them the sidebar only leaves out its tournament mode button.
      () => {}
    );
    return () => {
      current = false;
    };
  }, [clubId, version]);

  return loaded?.clubId === clubId ? loaded.tournaments : [];
}

export function useRefreshActiveTournaments() {
  const setVersion = useSetAtom(activeTournamentsVersion);
  return () => setVersion((version) => version + 1);
}
